import "./SolicitudesTable.css";

import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Modal, Spinner } from "react-bootstrap";
import { usePagination, useTable } from "react-table";

import { getApiUrl } from "../../utils/apiUrl";
import { getCsrfToken } from "../../utils/getCsrfToken";
import Loading from "../0. Utilidades/loadingSpinner";
import SolicitudDetailsModal from "./SolicitudDetailsModal";

// -------------------------------------------------------------------------------------------------------
// 0. HELPERS

function normalizeString(str) {
  return str
    ? str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
    : "";
}

// -------------------------------------------------------------------------------------------------------
// 1. MAIN COMPONENT

function SolicitudesTable() {
  const [searchQuery, setSearchQuery] = useState("");
  const [maestras, setMaestras] = useState([]);
  const [selectedRut, setSelectedRut] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoadingRut, setActionLoadingRut] = useState("");
  const [feedback, setFeedback] = useState(null);

  const [reminderConfirmation, setReminderConfirmation] = useState({
    show: false,
    solicitud: null,
  });

  // -----------------------------------------------------------------------------------------------------
  // 1.1 FETCH TABLE DATA

  useEffect(() => {
    const fetchMaestras = async () => {
      setIsLoading(true);

      try {
        const response = await fetch(
          getApiUrl("api/formularios/comunidades/maestras/"),
          {
            credentials: "include",
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.detail || "No se pudieron cargar las postulaciones.");
        }

        setMaestras(data);
      } catch (error) {
        console.error("Error fetching data:", error);

        setFeedback({
          type: "danger",
          message: error.message || "No se pudieron cargar las postulaciones.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaestras();
  }, []);

  const handleComentarioSaved = (rut, nuevoEstado) => {
    setMaestras((currentMaestras) =>
      currentMaestras.map((maestra) =>
        maestra.rut === rut
          ? {
              ...maestra,
              estado: nuevoEstado,
            }
          : maestra
      )
    );
  };

  // -----------------------------------------------------------------------------------------------------
  // 1.2 ACTIONS

  const handleAccepted = (rut) => {
    setMaestras((currentMaestras) =>
      currentMaestras.filter((maestra) => maestra.rut !== rut)
    );

    setSelectedRut("");
  };

  const handleDiscarded = (rut) => {
    setMaestras((currentMaestras) =>
      currentMaestras.filter((maestra) => maestra.rut !== rut)
    );

    setSelectedRut("");
  };

  const handleOpenDetails = (rut) => {
    setSelectedRut(rut);
  };

  const handleCloseDetails = () => {
    setSelectedRut("");
  };

  const handleOpenReminderConfirmation = (event, solicitud) => {
    event.stopPropagation();

    if (solicitud.avanzada_respondida) return;

    setReminderConfirmation({
      show: true,
      solicitud,
    });
  };

  const handleCloseReminderConfirmation = () => {
    setReminderConfirmation({
      show: false,
      solicitud: null,
    });
  };

  const handleSendReminder = async () => {
    const solicitud = reminderConfirmation.solicitud;

    if (!solicitud || solicitud.avanzada_respondida) {
      handleCloseReminderConfirmation();
      return;
    }

    setActionLoadingRut(solicitud.rut);
    setFeedback(null);

    try {
      const csrfToken = await getCsrfToken();
      
      const response = await fetch(
        getApiUrl(`/api/formularios/ingresos/avanzada/${solicitud.rut}/recordatorio/`),
        {
          method: "POST",
          headers: {
            "X-CSRFToken": csrfToken,
          },
          credentials: "include",
        }
      );

      const contentType = response.headers.get("content-type") || "";

      const data = contentType.includes("application/json")
        ? await response.json()
        : { message: "Respuesta inválida del servidor." };

      if (!response.ok) {
        throw new Error(data.message || "No se pudo enviar el recordatorio.");
      }

      setFeedback({
        type: "success",
        message: data.message || "Recordatorio enviado correctamente.",
      });

      handleCloseReminderConfirmation();
    } catch (error) {
      setFeedback({
        type: "danger",
        message: error.message || "No se pudo enviar el recordatorio.",
      });
    } finally {
      setActionLoadingRut("");
    }
  };

  

  // -----------------------------------------------------------------------------------------------------
  // 1.3 TABLE CONFIG

  const columns = useMemo(
    () => [
      {
        Header: "Fecha",
        accessor: "fecha_ingreso",
      },
      {
        Header: "Perfil",
        accessor: "perfil",
      },
      {
        Header: "Estado",
        accessor: "workflow_estado",
      },
      {
        Header: "Comentarios",
        accessor: "estado",
      },
      {
        Header: "Correos",
        accessor: "advanced_form_email_count",
      },
      {
        Header: "Acciones",
        accessor: "acciones",
      },
    ],
    []
  );

  const filteredData = useMemo(() => {
    const normalizedSearchTerm = normalizeString(searchQuery);

    return maestras
      .filter((maestra) => {
        const searchableText = [
          maestra.nombre,
          maestra.rut,
          maestra.comuna,
          maestra.comuna_label,
          maestra.oficio,
          maestra.oficio_label,
          maestra.fecha_ingreso,
          maestra.estado,
        ]
          .map((value) => normalizeString(value))
          .join(" ");

        return searchableText.includes(normalizedSearchTerm);
      })
      .map((maestra) => {
        const comuna = maestra.comuna_label || maestra.comuna || "—";
        const isReady = Boolean(maestra.avanzada_respondida);

        return {
          ...maestra,
          perfil: (
            <div className="solicitudes-profile-cell">
              <div className="solicitudes-profile-main">
                <span className="solicitudes-profile-name">
                  {maestra.nombre || "Sin nombre"}
                </span>
                <span className="solicitudes-profile-rut">{maestra.rut}</span>
              </div>

              <div className="solicitudes-profile-badges">
                <Badge bg="primary">{comuna}</Badge>
              </div>
            </div>
          ),
          workflow_estado: isReady ? (
            <Badge bg="success">
              Lista para entrevista
            </Badge>
          ) : (
            <Badge bg="warning">Pendiente 2da Encuesta</Badge>
          ),
          fecha_ingreso: maestra.fecha_ingreso || "—",
          advanced_form_email_count: (
            <span className="solicitudes-email-count">
              {maestra.advanced_form_email_count || 0}
            </span>
          ),
          estado: maestra.estado || "—",
          acciones: (
            <div className="solicitudes-actions">

              <Button
                size="sm"
                variant={isReady ? "outline-secondary" : "outline-success"}
                title={
                  isReady
                    ? "Ya respondió el formulario avanzado"
                    : "Enviar recordatorio"
                }
                disabled={isReady || actionLoadingRut === maestra.rut}
                onClick={(event) => handleOpenReminderConfirmation(event, maestra)}
              >
                {actionLoadingRut === maestra.rut ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "✉"
                )}
              </Button>
            </div>
          ),
          rowClassName: isReady ? "solicitudes-row-ready" : "",
        };
      });
  }, [maestras, searchQuery, actionLoadingRut]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        pageIndex: 0,
        pageSize: 8,
      },
    },
    usePagination
  );

  // -----------------------------------------------------------------------------------------------------
  // 1.4 SUMMARY VALUES

  const totalSolicitudes = maestras.length;
  const totalListas = maestras.filter((item) => item.avanzada_respondida).length;
  const totalPendientes = maestras.filter((item) => !item.avanzada_respondida).length;

  // -----------------------------------------------------------------------------------------------------
  // 1.5 RENDER

  return (
    <div className="solicitudes-table-container capsule">
      <div className="solicitudes-header">
        <div>
          <h5>Postulaciones a la Comunidad</h5>
          <p>
            Gestiona solicitudes de maestras, revisa formularios y envía recordatorios.
          </p>
        </div>

        <div className="solicitudes-summary">
          <div>
            <strong>{totalSolicitudes}</strong>
            <span>Total</span>
          </div>

          <div>
            <strong>{totalListas}</strong>
            <span>Listas</span>
          </div>

          <div>
            <strong>{totalPendientes}</strong>
            <span>Pendientes</span>
          </div>
        </div>
      </div>

      {feedback && (
        <Alert
          variant={feedback.type}
          className="solicitudes-feedback"
          dismissible
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      <div className="solicitudes-table-wrapper">
        <div className="solicitudes-toolbar">
          <div className="solicitudes-search-bar">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre, RUT, comuna, oficio..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />

            {searchQuery && (
              <button
                type="button"
                className="clear-search-button"
                onClick={() => setSearchQuery("")}
              >
                &times;
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <Loading />
        ) : (
          <>
            <div className="solicitudes-pagination">
              <div>
                <select
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                >
                  {[5, 8, 10, 20, 30, 50].map((size) => (
                    <option key={size} value={size}>
                      Mostrar {size}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                  {"<"}
                </button>

                <span>
                  Página {pageIndex + 1} de {pageOptions.length || 1}
                </span>

                <button onClick={() => nextPage()} disabled={!canNextPage}>
                  {">"}
                </button>
              </div>
            </div>

            <div className="solicitudes-table-scroll">
              <table {...getTableProps()} className="solicitudes-table">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);

                    return (
                      <tr
                        {...row.getRowProps()}
                        className={row.original.rowClassName}
                        onClick={() => handleOpenDetails(row.original.rut)}
                      >
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Modal
        show={reminderConfirmation.show}
        onHide={handleCloseReminderConfirmation}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar envío de recordatorio</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">
            ¿Seguro que quieres enviar el recordatorio del formulario avanzado?
          </p>

          <div className="solicitudes-reminder-confirmation-box">
            <strong>
              {reminderConfirmation.solicitud?.nombre || "Postulante sin nombre"}
            </strong>

            <span>
              RUT: {reminderConfirmation.solicitud?.rut || "—"}
            </span>
          </div>

          <p className="text-muted mt-3 mb-0">
            Se enviará un correo electrónico con el enlace para completar el registro.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={handleCloseReminderConfirmation}
            disabled={Boolean(actionLoadingRut)}
          >
            Cancelar
          </Button>

          <Button
            variant="success"
            onClick={handleSendReminder}
            disabled={Boolean(actionLoadingRut)}
          >
            {actionLoadingRut ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Enviando...
              </>
            ) : (
              "Enviar recordatorio"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <SolicitudDetailsModal
        show={Boolean(selectedRut)}
        rut={selectedRut}
        onHide={handleCloseDetails}
        onComentarioSaved={handleComentarioSaved}
        onAccepted={handleAccepted}
        onDiscarded={handleDiscarded}
      />
    </div>
  );
}

export default SolicitudesTable;
